import React, { useState, useEffect } from "react";
import { Form, FormGroup, Label, Input, Row, Col, Button } from "reactstrap";
import Switch from "react-bootstrap-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Select from 'react-select'

const AdSettings = ({ adConfig, formDetails, setFormDetails }) => {
    console.log("adConfig in adsSettings", adConfig);
    console.log("formDetails in adsSettings", formDetails);
    const [collapsedItems, setCollapsedItems] = useState({});
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [draggingIndex, setDraggingIndex] = useState(null);
    
    // Add an effect to ensure textareas are resizable after component mounts
    useEffect(() => {
        // Force all textareas to be resizable
        const makeTextareasResizable = () => {
            const textareas = document.querySelectorAll('textarea');
            textareas.forEach(textarea => {
                textarea.style.resize = 'both';
                
                // Create and inject a resize observer to handle dynamic size changes
                const resizeObserver = new ResizeObserver(() => {
                    // When textarea resizes, ensure parent containers adjust
                    let parent = textarea.parentElement;
                    while (parent) {
                        if (parent.style) {
                            parent.style.overflow = 'visible';
                        }
                        parent = parent.parentElement;
                    }
                });
                
                resizeObserver.observe(textarea);
            });
        };
        
        // Run immediately and also after a short delay to handle any lazy loading
        makeTextareasResizable();
        setTimeout(makeTextareasResizable, 500);
        
        // Add event listeners to handle any dynamically added textareas
        const observer = new MutationObserver(makeTextareasResizable);
        observer.observe(document.body, { childList: true, subtree: true });
        
        return () => observer.disconnect();
    }, []);


    const shouldDisplayField = (fieldConfig) => {
        if (!fieldConfig.conditional) return true;

        const { field, value } = fieldConfig.conditional;

        const current =
            formDetails[field] ??
            formDetails.widget_data?.ads_data?.[field];

        if (Array.isArray(value)) return value.includes(current);
        return current === value;
    };

    const handleDragStart = (index) => setDraggingIndex(index);
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = (index) => {
        if (draggingIndex === null) return;
        setFormDetails((prev) => {
            const reordered = [...prev.widget_data.match_order];
            const dragged = reordered.splice(draggingIndex, 1)[0];
            reordered.splice(index, 0, dragged);
            return {
                ...prev,
                widget_data: {
                    ...prev.widget_data,
                    match_order: reordered,
                },
            };
        });
        setDraggingIndex(null);
    };

    const renderField = (key, fieldConfig) => {
        if (!shouldDisplayField(fieldConfig)) return null;

        switch (fieldConfig.type) {
            case "text":
            case "number":
                return (
                    <Input
                        type={fieldConfig.type}
                        value={formDetails.widget_data?.ads_data[key] || ""}
                        placeholder={fieldConfig.label}
                        onChange={(e) =>
                            setFormDetails((prev) => ({
                                ...prev,
                                widget_data: {
                                    ...prev.widget_data,
                                    ads_data: {
                                        ...prev.widget_data.ads_data,
                                        [key]: e.target.value,
                                    },
                                },
                            }))
                        }
                    />
                );

            case "textArea":
                return (
                    <div className="custom-textarea-container">
                        <textarea
                            className="custom-resizable-textarea"
                            value={formDetails.widget_data?.ads_data[key] || ""}
                            onChange={(e) =>
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: {
                                        ...prev.widget_data,
                                        ads_data: {
                                            ...prev.widget_data.ads_data,
                                            [key]: e.target.value,
                                        },
                                    },
                                }))
                            }
                        />
                    </div>
                );

            case "switch":
                return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Switch
                            offColor="danger"
                            onColor="success"
                            offText={<i className="nc-icon nc-simple-remove" />}
                            onText={<i className="nc-icon nc-check-2" />}
                            value={formDetails?.widget_data?.ads_data[key] || false}
                            onChange={() =>
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: {
                                        ...prev.widget_data,
                                        ads_data: {
                                            ...prev.widget_data.ads_data,
                                            [key]: !prev.widget_data.ads_data[key],
                                        },
                                    },
                                }))
                            }
                        />
                    </div>
                );

            case "radio":
                return fieldConfig.options.map((option) => (
                    <Label key={option} check className="d-flex align-items-center text-start gap-1">
                        <Input
                            type="radio"
                            name={key}
                            value={option}
                            checked={formDetails.widget_data?.ads_data[key] === option}
                            onChange={(e) =>
                                setFormDetails((prev) => ({
                                    ...prev,
                                    widget_data: {
                                        ...prev.widget_data,
                                        ads_data: {
                                            ...prev.widget_data.ads_data,
                                            [key]: e.target.value,
                                        },
                                    },
                                }))
                            }
                        />
                        {option}
                    </Label>
                ));

            case "drag-drop":
                return (
                    <FormGroup>
                        <div className="border p-2 rounded bg-white d-flex align-items-center" style={{ cursor: "grab" }}>
                            {(formDetails.widget_data?.ads_data?.match_order || []).map((option, idx) => (
                                <React.Fragment key={option}>
                                    <span
                                        draggable
                                        onDragStart={() => handleDragStart(idx)}
                                        onDragOver={handleDragOver}
                                        onDrop={() => handleDrop(idx)}
                                        className="px-2 py-1 rounded bg-light border"
                                    >
                                        {option}
                                    </span>
                                    {idx !== formDetails.widget_data?.ads_data.match_order.length - 1 && (
                                        <span className="mx-2 text-muted">{">"}</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </FormGroup>
                );

                case "array":
                    if (!Array.isArray(formDetails.widget_data?.ads_data[key])) {
                        setFormDetails((prev) => ({
                            ...prev,
                            widget_data: {
                                ...prev.widget_data,
                                ads_data: {
                                    ...prev.widget_data.ads_data,
                                    [key]: [],
                                },
                            },
                        }));
                        return null;
                    }

                    return (
                        <Row className="mb-3">
                            <Col>
                                {(formDetails.widget_data?.ads_data[key] || []).map((item, index) => (
                                    <FormGroup key={index} className="mb-3">
                                        <div className="border rounded p-3 bg-white w-100 d-flex flex-column">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong>{`Ad Block ${index + 1}`}</strong>
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        const updatedArray = formDetails.widget_data?.ads_data[key].filter((_, i) => i !== index);
                                                        setFormDetails((prev) => ({
                                                            ...prev,
                                                            widget_data: {
                                                                ...prev.widget_data,
                                                                ads_data: {
                                                                    ...prev.widget_data.ads_data,
                                                                    [key]: updatedArray,
                                                                },
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    Delete
                                                </Button>
                                            </div>

                                            <Row className="align-items-start mb-3">
                                                <Col sm="3"><Label className="mb-0 text-start">Ad Code</Label></Col>
                                                <Col sm="9" style={{ overflow: "visible" }}>
                                                    <div className="custom-textarea-container">
                                                        <textarea
                                                            className="custom-resizable-textarea"
                                                            style={{ minHeight: "120px" }}
                                                            value={item.adcode}
                                                            onChange={(e) => {
                                                                const updatedArray = [...formDetails.widget_data?.ads_data[key]];
                                                                updatedArray[index].adcode = e.target.value;
                                                                setFormDetails((prev) => ({
                                                                    ...prev,
                                                                    widget_data: {
                                                                        ...prev.widget_data,
                                                                        ads_data: {
                                                                            ...prev.widget_data.ads_data,
                                                                            [key]: updatedArray,
                                                                        },
                                                                    },
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="align-items-center mb-2">
                                                <Col sm="3"><Label className="mb-0 text-start">Position</Label></Col>
                                                <Col sm="9">
                                                    <Select
                                                        options={fieldConfig?.item_structure?.position?.options || []}
                                                        value={
                                                            item.position !== undefined
                                                                ? fieldConfig?.item_structure?.position?.options.find(
                                                                    (opt) => opt.value === item.position
                                                                )
                                                                : null
                                                        }
                                                        placeholder="Select Position"
                                                        onChange={(selected) => {
                                                            const updatedArray = [...formDetails.widget_data?.ads_data[key]];
                                                            updatedArray[index].position = selected?.value || "";
                                                            setFormDetails((prev) => ({
                                                                ...prev,
                                                                widget_data: {
                                                                    ...prev.widget_data,
                                                                    ads_data: {
                                                                        ...prev.widget_data.ads_data,
                                                                        [key]: updatedArray,
                                                                    },
                                                                },
                                                            }));
                                                        }}
                                                        isClearable
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    </FormGroup>
                                ))}

                                {/* Add New Button */}
                                <div className="text-start mt-2">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => {
                                            setFormDetails((prev) => ({
                                                ...prev,
                                                widget_data: {
                                                    ...prev.widget_data,
                                                    ads_data: {
                                                        ...prev.widget_data.ads_data,
                                                        [key]: [
                                                            ...(prev.widget_data.ads_data[key] || []),
                                                            { adcode: "", position: "" },
                                                        ],
                                                    },
                                                },
                                            }));
                                        }}
                                    >
                                        Add Ad Block
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    );

            default:
                return null;
        }
    };

    return (
        <Form className="resizable-form">
            <style>
                {`
                    .custom-resizable-textarea {
                        min-height: 100px;
                        min-width: 100%;
                        width: 100%;
                        resize: both;
                        border: 1px solid #ced4da;
                        border-radius: 0.25rem;
                        padding: 0.375rem 0.75rem;
                        font-size: 1rem;
                        line-height: 1.5;
                        color: #495057;
                        background-color: #fff;
                    }
                    
                    .custom-resizable-textarea:focus {
                        border-color: #80bdff;
                        outline: 0;
                        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                    }
                    
                    .custom-textarea-container {
                        position: relative;
                        width: 100%;
                        overflow: visible;
                        margin-bottom: 1rem;
                    }
                    
                    /* Override all possible restrictions */
                    .row, .col, .form-group, .form-control {
                        overflow: visible !important;
                    }
                    
                    /* Ensure all containing elements don't limit resizing */
                    body, #root, .container, .container-fluid {
                        overflow: visible !important;
                    }
                `}
            </style>
            <Row>
                <Col>
                    {Object.entries(adConfig.items).map(([key, fieldConfig]) =>
                        shouldDisplayField(fieldConfig) ? (
                            <FormGroup key={key} className="px-4 mt-3 resizable-form-group">
                                <Row className="align-items-start" style={{ overflow: "visible" }}>
                                    {fieldConfig.label && (
                                        <Col sm="3">
                                            <div className="designLabel">
                                                <small className="text-default text-start">{fieldConfig.label}</small>
                                            </div>
                                        </Col>
                                    )}
                                    <Col sm={fieldConfig.label ? "6" : "12"} style={{ overflow: "visible" }}>
                                        {renderField(key, fieldConfig)}
                                    </Col>
                                </Row>
                            </FormGroup>
                        ) : null
                    )}
                </Col>
            </Row>
        </Form>
    );
};

export default AdSettings;